// Fuel tank level for the live popups.
//
// The number in the payload is already a percent: the firmware reads OBD PID
// 0x2F and converts on-device (100/255*A), and sends 0xFF when the car does not
// answer, which the gateway drops before it is ever stored. The API gates the
// field on obd_present and sends null when there is nothing to show.
//
// So null and 0 are two different facts - "this car never told us" versus "the
// tank is empty" - and collapsing them either hides an empty tank or invents
// one on every phone. Everything here keeps them apart.
//
// Deliberately free of imports: obd_present is the exact condition the server
// gates on, so it is both a better client-side check than inferring from the
// asset kind and one that leaves this module unit-testable on its own.

// Percent 0-100, or null when there is no reading to show.
export function fuelLevel(device) {
    // Belt and suspenders. The server already nulls fuel_level unless the SKU
    // reports OBD, so a phone/pet/item tracker can never reach the rest of this.
    if (device?.obd_present !== true) return null;

    const raw = device?.fuel_level;
    // Explicit null/undefined check, NOT a truthiness test: `if (!raw)` would
    // throw away a genuinely empty tank, which is the one reading worth seeing.
    if (raw === null || raw === undefined || raw === "") return null;

    const pct = Number(raw);
    if (!Number.isFinite(pct)) return null;
    // Range guard so a raw byte from a pre-gateway-fix row cannot render "255%".
    if (pct < 0 || pct > 100) return null;

    return Math.round(pct);
}

// Chip colour for a tank level.
//
// Thresholds match the fleet portal (<=15 red, <=30 amber) so the same car is
// not called "low" in one product and "fine" in the other. Above that it stays
// grey rather than green: a three-quarter tank is not an achievement, and a row
// of green buries the readings that matter.
export function fuelColor(pct) {
    if (pct === null) return "#666";
    if (pct <= 15) return "#d70000";
    if (pct <= 30) return "#F97316";
    return "#666";
}

// Tooltip. Never claims the reading is current - fuel rides the position
// packet, so it is only as fresh as the device's last report.
export function fuelHint(device) {
    const pct = fuelLevel(device);
    if (pct === null) return "";
    if (pct === 0) return "Fuel tank empty, as of this tracker's last report";
    if (pct <= 15) return `Fuel low (${pct}%), as of this tracker's last report`;
    return `Fuel ${pct}%, as of this tracker's last report`;
}
